/**
 * Encodes an alias for a specific scope/type
 */
export type ScopedAlias = `${ScopedAlias.Scope}.${string}`
export namespace ScopedAlias {
  export type Scope = (typeof Scope.ALL)[number]
  export namespace Scope {
    export const ALL = ['tenant', 'zone'] as const
  }

  /**
   * Represents the information encoded in {@link ScopedAlias} in a way that is
   * easier to use
   */
  export type AsObject = ExactlyOneOf<Record<Scope, string>>

  /**
   * Creates a {@link ScopedAlias} from a {@link ScopedAlias.AsObject}
   */
  export function from(object: AsObject): ScopedAlias
  export function from(scope: Scope, alias: string): ScopedAlias
  export function from(object: Partial<AsObject>): ScopedAlias | undefined
  export function from(
    objectOrScope: Partial<AsObject> | Scope,
    alias?: string,
  ): ScopedAlias | undefined {
    if (typeof objectOrScope === 'string') {
      return `${objectOrScope}.${alias}`
    }
    for (const scope of Scope.ALL) {
      alias = objectOrScope[scope]
      if (alias) {
        return `${scope}.${alias}`
      }
    }
    return undefined
  }

  /**
   * Parses a {@link ScopedAlias} to a {@link ScopedAlias.AsObject}
   */
  export function parse(scopedAlias: Scope): AsObject
  export function parse(scopedAlias: string): AsObject | undefined
  export function parse(scopedAlias: string): AsObject | undefined {
    for (const scope of Scope.ALL) {
      const prefix = `${scope}.`
      if (scopedAlias.startsWith(prefix)) {
        return {
          [scope]: scopedAlias.substring(prefix.length),
        } satisfies Partial<AsObject> as Partial<AsObject> as AsObject
      }
    }
    return undefined
  }
}
